import AppleLogo from 'assets/landing-page/apple-white.svg'
import { Button } from 'common/components/Button/Button'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ApiPathsSocial, socialAppleLogin } from '../social.service'
import { useAppleSignIn } from '../useAppleSignIn'
import { useSocialLoginFlow } from '../useSocialLoginFlow'

interface IAppleSignInButtonProps {
  redirectTo?: string
  /** Disables the button, e.g. while the email/password form or the Google button is mid-flow. */
  disabled?: boolean
  /** Reports whether this button is currently mid-flow, so sibling login controls can disable themselves. */
  onLoadingChange?: (isLoading: boolean) => void
}

export const AppleSignInButton = ({ redirectTo, disabled, onLoadingChange }: IAppleSignInButtonProps) => {
  const { isReady, loadError, signIn } = useAppleSignIn()
  const [isSigningIn, setIsSigningIn] = useState(false)

  const { trigger: triggerSocialLogin, isMutating } = useSocialLoginFlow(ApiPathsSocial.apple, socialAppleLogin, {
    redirectTo,
    genericErrorMessage: 'Apple sign-in failed. Please try again.',
    extraErrorHandler(err) {
      if (err.response?.status === 401) {
        toast.error('Apple did not share your email. Please allow email sharing and try again.')
        return true
      }
      return false
    },
  })

  const isLoading = isSigningIn || isMutating

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleClick = async () => {
    try {
      setIsSigningIn(true)
      const { idToken, nonce, user } = await signIn()
      await triggerSocialLogin({ idToken, nonce, user }, { throwOnError: false })
    } catch {
      // User cancelled the Apple popup — no error toast, matches spec §9.
    } finally {
      setIsSigningIn(false)
    }
  }

  return (
    <Button
      type="button"
      variant="black"
      className="w-full gap-2 border-round-lg"
      size="large"
      disabled={disabled || !isReady || loadError}
      isLoading={isLoading}
      onClick={handleClick}
      data-cy="social-login-apple"
    >
      <AppleLogo style={{ width: '18px', height: '20px' }} /> Continue with Apple
    </Button>
  )
}
